import { InputWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Typography,
  useTheme
} from '@material-ui/core'
import WatchLaterIcon from '@material-ui/icons/WatchLater'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import { BOOKING_DETAILS_FOR_ROOM_STAY_QUERY } from '../../graphql/bookingDetail'
import {
  CANCEL_RESERVATION_TA_PANEL_MUTATION,
  CANCEL_RESERVATION_TA_PANEL_PREVIEW_QUERY
} from '../../graphql/cancelReservationTAPanel'
import {
  CANCEL_RESERVATION_TA_PANEL_ROOM_STAY_MUTATION,
  CANCEL_RESERVATION_TA_PANEL_ROOM_STAY_PREVIEW_QUERY
} from '../../graphql/cancelReservationTAPanelRoomStay'
import { CONFIRM_MODIFICATION_TA_PANEL_MUTATION } from '../../graphql/modifyReserveTAPanel'
import { addCommaToPrice, removeCommaFromPrice } from '../../helpers/numbers'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { ConfirmReserveResponse } from '../../types/ConfirmReserve'
import { PMSStatus } from '../../types/booking'
import { BookingDetailForRoomStay } from '../../types/bookingDetails'
import {
  CancelReservationInput,
  CancelReservationResponse,
  CancelRoomStayInfo,
  CancelRoomStayInput,
  CancelRoomStayResponse
} from '../../types/cancelReservationTAPanel'
import { Currency } from '../../types/currency'
import { ModifyReserveResponse } from '../../types/modifyReserveTAPanel'
import BookingReceipt from '../BookingStepper/components/BookingReceipt'
import RoomStayInformationForCancel from './components/RoomStayInformation'
import useStyles from './styles/index.styles'

const ChangeReservationConfirm = () => {
  const classes = useStyles()
  const params = useParams<{
    reserveNumber: string
    page: 'cancel-reserve' | 'cancel-room-stay' | 'modify-room-stay'
    roomReservationNumber?: string
  }>()
  const theme = useTheme()
  const [ReceiptMode, setReceiptMode] =
    useState<Extract<PMSStatus, 'Cancelled' | 'ModificationReserved'>>(
      'Cancelled'
    )
  const [showBookingReceiptdModal, setShowBookingReceiptModal] = useState(false)
  const [penaltyAmount, setPenaltyAmount] = useState(0)
  const [currency, setCurrency] = useState<Currency>('IRR')
  const [enableOverRiden, setEnableOverRiden] = useState(false)
  const [cancelReservationInput, setCancelReservationInput] =
    useState<CancelReservationInput>({
      dryRun: '0',
      cancelReason: 'test',
      cancellationPolicyOverridden: false,
      overriddenCancellationPenalty: 0,
      reservationNumber: params.reserveNumber
    })
  const zeroTime = '00:00'
  const [timeLeft, setTimeLeft] = useState('')
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [modifiedRoom, setModifiedRoom] = useState<CancelRoomStayInfo>()

  const [cancelRoomStayInput, setCancelRoomStayInput] =
    useState<CancelRoomStayInput>({
      dryRun: '0',
      reservationNumber: params.reserveNumber,
      roomReservationNumber: params.roomReservationNumber || '',
      cancelReason: '',
      cancellationPolicyOverridden: false,
      overriddenCancellationPenalty: 0
    })

  const [recieptModalData] = useState<ConfirmReserveResponse>({
    reservationNumber:
      params.reserveNumber || params.roomReservationNumber || '',
    displayReservationNumber: '',
    description: '',
    status: {
      PMS: 'Cancelled',
      CRS: 'Cancelled'
    },
    wallet: '',
    paymentDate: '',
    providerName: '',
    additionalDiscount: 0,
    totalDiscount: 0,
    subtotalBeforeDiscount: 0,
    subtotalAfterDiscount: 0,
    totalPayable: 0,
    roomStaysReserveDetails: []
  })

  const validRolesForOverRide = [
    ROLE.ADMIN,
    ROLE.PROVIDER,
    ROLE.PROVIDER_MARKETING,
    ROLE.ACCOMMODATION
  ]

  const isAutenticatedForOverride = validRolesForOverRide.includes(
    getRoleFromStorage()
  )

  useEffect(() => {
    setCancelRoomStayInput((prev) => ({
      ...prev,
      cancellationPolicyOverridden: enableOverRiden
    }))

    setCancelReservationInput((prev) => ({
      ...prev,
      cancellationPolicyOverridden: enableOverRiden
    }))
  }, [enableOverRiden])

  //BOOKING_DETAILS_FOR_ROOMSTAY
  const {
    data: bookingDetailForRoomStayData,
    loading: loadingForBookingDetails
  } = useQuery<{
    bookingDetailForRoomStay: BookingDetailForRoomStay
  }>(BOOKING_DETAILS_FOR_ROOM_STAY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: params.page !== 'modify-room-stay',
    variables: {
      reservationNumber: params.reserveNumber,
      roomReservationNumber: params.roomReservationNumber
    },
    onCompleted: (res) => {
      setExpiresAt(res.bookingDetailForRoomStay.basicDetails.expiredAt || null)
      const newRoom: CancelRoomStayInfo = {
        roomTypeName: res.bookingDetailForRoomStay.room.roomTypeName,
        roomTypeCode: res.bookingDetailForRoomStay.room.roomTypeCode,
        displayRoomTypeCode:
          res.bookingDetailForRoomStay.room.displayRoomTypeCode,
        displayRatePlanCode:
          res.bookingDetailForRoomStay.room.displayRatePlanCode,
        ratePlanName: res.bookingDetailForRoomStay.room.ratePlanName,
        ratePlanCode: res.bookingDetailForRoomStay.room.ratePlanCode,
        guests: res.bookingDetailForRoomStay.room.profiles.map((p) => ({
          name: p.firstName,
          email: p.email,
          mobile: p.phone
        })),
        numberOfGuests: res.bookingDetailForRoomStay.room.numberOfAdults,
        numberOfExtrabeds: res.bookingDetailForRoomStay.room.addBed,
        numberOfChildren:
          res.bookingDetailForRoomStay.room.occupancy?.children || 0,
        checkIn: res.bookingDetailForRoomStay.room.checkIn,
        checkOut: res.bookingDetailForRoomStay.room.checkOut,
        status: res.bookingDetailForRoomStay.room.pmsStatus,
        oldPrice: res.bookingDetailForRoomStay.room.originalPrice,
        currentPrice: res.bookingDetailForRoomStay.room.payablePrice,
        currency: res.bookingDetailForRoomStay.basicDetails.currency,
        penalty: res.bookingDetailForRoomStay.room.changePenalty,
        payable: res.bookingDetailForRoomStay.room.modificationDiff
      }

      setModifiedRoom(newRoom)
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  useEffect(() => {
    if (
      !bookingDetailForRoomStayData?.bookingDetailForRoomStay.basicDetails
        .canConfirm ||
      !expiresAt
    )
      return
    const expiry = new Date(expiresAt.replace(' ', 'T'))
    const updateTimer = () => {
      const now = new Date()
      const diff = expiry.getTime() - now.getTime()

      if (diff <= 0) {
        setTimeLeft(zeroTime)
        return
      }

      const minutes = Math.floor(diff / 1000 / 60)
      const seconds = Math.floor((diff / 1000) % 60)
      setTimeLeft(
        `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
          2,
          '0'
        )}`
      )
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [expiresAt])

  //CANCEl_RERERVATION_PREVIEW
  const {
    data: canceReservationPreviewData,
    loading: loadingForCancelPreviewData
  } = useQuery<{
    cancelReservationTAPanelPreview: CancelRoomStayInfo[]
  }>(CANCEL_RESERVATION_TA_PANEL_PREVIEW_QUERY, {
    skip: params.page !== 'cancel-reserve',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: { reservationNumber: params.reserveNumber },
    onCompleted: (res) => {
      setCurrency(res.cancelReservationTAPanelPreview[0].currency)
    },
    onError: (err) => toast.error(err.message)
  })

  //CANCEl_ROOMSTAY_PREVIEW
  const {
    data: cancelRoomStayPreviewData,
    loading: loadingForCancelRoomStayPreview
  } = useQuery<{
    cancelReservationTAPanelRoomStayPreview: CancelRoomStayInfo
  }>(CANCEL_RESERVATION_TA_PANEL_ROOM_STAY_PREVIEW_QUERY, {
    skip: params.page !== 'cancel-room-stay',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      reservationNumber: params.reserveNumber,
      roomReservationNumber: params.roomReservationNumber
    },
    onCompleted: (res) => {
      setCurrency(res.cancelReservationTAPanelRoomStayPreview.currency)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  //CANCEL_RESERVATION
  const [cancelReservation, { loading: loadingForCancel }] = useMutation<{
    cancelReservationTAPanel: CancelReservationResponse
  }>(CANCEL_RESERVATION_TA_PANEL_MUTATION, {
    onCompleted: (data) => {
      toast.success(data.cancelReservationTAPanel.description)
      setShowBookingReceiptModal(true)
      if (cancelReservationInput.dryRun === '1') {
        setCancelReservationInput({
          ...cancelReservationInput,
          dryRun: '0'
        })
      }
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  //CANCEL_ROOM_STAY
  const [cancelRoomStay, { loading: loadingForCancelRoomStay }] = useMutation<{
    CancelReservationTAPanelRoomStay: CancelRoomStayResponse
  }>(CANCEL_RESERVATION_TA_PANEL_ROOM_STAY_MUTATION, {
    onCompleted: () => {
      setShowBookingReceiptModal(true)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  //CONFIRM_MODIFICATION_MUTATION
  const [confirmModification, { loading: loadingForConfirmModification }] =
    useMutation<{ ConfirmModificationTAPanel: ModifyReserveResponse }>(
      CONFIRM_MODIFICATION_TA_PANEL_MUTATION,
      {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted: () => {
          setReceiptMode('ModificationReserved')
          setShowBookingReceiptModal(true)
        }
      }
    )

  const loadingForCircularProgress =
    loadingForCancel ||
    loadingForCancelRoomStay ||
    loadingForConfirmModification

  return (
    <div className={classes.root}>
      {(cancelRoomStayPreviewData?.cancelReservationTAPanelRoomStayPreview ||
        canceReservationPreviewData?.cancelReservationTAPanelPreview ||
        modifiedRoom) && (
        <div className={classes.top}>
          <div className={classes.herderTitle}>
            <Typography variant="h3" className={classes.herderTitle}>
              {getTranslate(' Check Out')}
            </Typography>
            <Typography variant="h5" className={classes.greyText}>
              {getTranslate('Book a hotel/ Modify Booking/ Check Out')}
            </Typography>
          </div>
          {params.page === 'modify-room-stay' && (
            <div className={classes.expiryContainer}>
              <Typography variant="h5" className={classes.verticalAlign}>
                <WatchLaterIcon color="primary" className={classes.icon} />
                {getTranslate(
                  timeLeft === zeroTime ? 'Expired' : 'Expire Time'
                )}
              </Typography>
              <Typography
                variant="h5"
                className={classes.timeText}
                style={{ direction: lang == 'fa' ? 'ltr' : 'ltr' }}>
                {timeLeft}
              </Typography>
            </div>
          )}
        </div>
      )}
      {params.page === 'cancel-reserve' &&
        canceReservationPreviewData?.cancelReservationTAPanelPreview.map(
          (room, index) => (
            <RoomStayInformationForCancel room={room} key={index} />
          )
        )}
      {params.page === 'cancel-room-stay' &&
        cancelRoomStayPreviewData?.cancelReservationTAPanelRoomStayPreview && (
          <RoomStayInformationForCancel
            room={
              cancelRoomStayPreviewData.cancelReservationTAPanelRoomStayPreview
            }
          />
        )}
      {params.page === 'modify-room-stay' && modifiedRoom && (
        <RoomStayInformationForCancel room={modifiedRoom} />
      )}
      {(cancelRoomStayPreviewData?.cancelReservationTAPanelRoomStayPreview ||
        canceReservationPreviewData?.cancelReservationTAPanelPreview ||
        modifiedRoom) && (
        <div className={classes.bottom}>
          {modifiedRoom ? (
            <div />
          ) : (
            <div className={classes.left}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableOverRiden}
                    disabled={!isAutenticatedForOverride}
                    color="primary"
                    size="small"
                    onChange={(e) => {
                      setEnableOverRiden(e.target.checked)
                    }}
                  />
                }
                label={getTranslate('Override Cancel Penalty')}
                classes={{ label: classes.label }}
              />
              {enableOverRiden && (
                <InputWithLabel
                  type="text"
                  noHelperText
                  placeholder={getTranslate('Enter Overridden price')}
                  inputSize="small"
                  value={addCommaToPrice(penaltyAmount)}
                  onChange={(e) => {
                    const newValue = e.currentTarget.value
                    const commaLessValue = removeCommaFromPrice(newValue)
                    setPenaltyAmount(Number(commaLessValue))
                    if (params.page === 'cancel-reserve') {
                      setCancelReservationInput({
                        ...cancelReservationInput,
                        cancellationPolicyOverridden: true,
                        overriddenCancellationPenalty: Number(commaLessValue)
                      })
                    } else if (params.page === 'cancel-room-stay') {
                      setCancelRoomStayInput({
                        ...cancelRoomStayInput,
                        cancellationPolicyOverridden: true,
                        overriddenCancellationPenalty: Number(commaLessValue)
                      })
                    }
                  }}
                  endIcon={
                    <InputAdornment position="end">
                      {getTranslate(currency || '')}
                    </InputAdornment>
                  }
                />
              )}
            </div>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.btn}
            disabled={
              loadingForCircularProgress ||
              (modifiedRoom && timeLeft === zeroTime)
            }
            onClick={() => {
              if (params.page === 'cancel-reserve') {
                cancelReservation({ variables: cancelReservationInput })
              } else if (params.page === 'cancel-room-stay') {
                cancelRoomStay({ variables: cancelRoomStayInput })
              } else if (params.page === 'modify-room-stay') {
                confirmModification({
                  variables: {
                    reservationNumber: params.reserveNumber,
                    roomReservationNumber: params.roomReservationNumber
                  }
                })
              }
            }}>
            <Typography className={classes.btnText}>
              {getTranslate('Confirm')}
            </Typography>
            {loadingForCircularProgress && (
              <CircularProgress
                className={classes.circularProgress}
                size={16}
              />
            )}
          </Button>
        </div>
      )}

      <BookingReceipt
        mode={ReceiptMode}
        open={showBookingReceiptdModal}
        onClose={() => {}}
        data={recieptModalData}
        reservationNumber={params.reserveNumber || params.roomReservationNumber}
      />
      <AdotelSkeleton
        open={
          loadingForCancelPreviewData ||
          loadingForCancelRoomStayPreview ||
          loadingForBookingDetails
        }
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
    </div>
  )
}

export default ChangeReservationConfirm
